import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Plus, X, MapPin, Calendar, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, photoUrl } from "@/lib/api";
import FilterBar from "@/components/FilterBar";
import { ListingCard } from "@/components/BrowseCards";
import PostListingSheet from "@/pages/PostListingSheet";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function toAbsolute(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/photos/")) return photoUrl(url.replace("/api/photos/", ""));
    return `${BACKEND_URL}${url}`;
}

const EMPTY = { city: "", budget_min: "", budget_max: "", move_in_after: "", gender: "Any", vibe_tags: [] };

export default function FindFlatsTab() {
    const [filters, setFilters] = useState(EMPTY);
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.city) params.city = filters.city;
            if (filters.budget_min) params.budget_min = filters.budget_min;
            if (filters.budget_max) params.budget_max = filters.budget_max;
            if (filters.move_in_after) params.move_in_after = filters.move_in_after;
            if (filters.gender && filters.gender !== "Any") params.gender = filters.gender;
            const { data } = await api.get("/listings", { params });
            setListings(data.listings || []);
        } catch (e) {
            toast.error("Couldn't load flats");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="mx-auto max-w-xl px-5 pt-10 pb-24" data-testid="flats-tab">
            <div className="flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-3 py-1.5 text-xs font-semibold w-fit">
                <Home className="h-3.5 w-3.5" strokeWidth={2.5} /> flats near you
            </div>
            <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter"
            >
                Find your <span className="font-fancy text-gradient-purple">flat</span>
            </motion.h1>
            <p className="mt-2 text-muted-foreground">Real listings from real humans. Post yours in 60s.</p>

            <div className="mt-6">
                <FilterBar filters={filters} setFilters={setFilters} testIdPrefix="flats-filter" />
            </div>

            <div className="mt-4">
                <div className="text-xs font-semibold text-muted-foreground">
                    {loading ? "Loading…" : `${listings.length} listing${listings.length === 1 ? "" : "s"}`}
                </div>
            </div>

            <div className="mt-3 space-y-3">
                {!loading && listings.length === 0 ? (
                    <div className="rounded-3xl border border-border bg-card p-8 text-center" data-testid="flats-empty">
                        <div className="mx-auto h-12 w-12 rounded-2xl bg-accent text-accent-foreground grid place-items-center">
                            <Home className="h-6 w-6" strokeWidth={2.5} />
                        </div>
                        <div className="mt-4 font-display text-lg font-bold">No listings match</div>
                        <p className="text-sm text-muted-foreground mt-1">Try widening your filters — or be the first to post here.</p>
                    </div>
                ) : (
                    listings.map((l, i) => (
                        <motion.div key={l.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.02 * i }}>
                            <ListingCard listing={l} onClick={() => setDetail(l)} testId={`flat-card-${l.id}`} />
                        </motion.div>
                    ))
                )}
            </div>

            {/* Floating "post" button (offset above bottom nav) */}
            <button
                onClick={() => setPosting(true)}
                data-testid="post-listing-fab"
                className="fixed bottom-24 right-5 z-40 h-14 rounded-full bg-primary text-primary-foreground px-5 shadow-[0_15px_35px_-10px_rgba(139,92,246,0.7)] flex items-center gap-2 font-semibold lift"
            >
                <Plus className="h-5 w-5" strokeWidth={3} /> Post flat
            </button>

            <PostListingSheet open={posting} onClose={() => setPosting(false)} onCreated={load} />
            <ListingDetail listing={detail} onClose={() => setDetail(null)} />
        </div>
    );
}

function ListingDetail({ listing, onClose }) {
    if (!listing) return null;
    const cover = toAbsolute(listing.cover_url);
    return (
        <AnimatePresence>
            {listing && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 z-[70] bg-background/80 backdrop-blur-xl"
                    data-testid="listing-detail"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-x-0 bottom-0 mx-auto max-w-xl rounded-t-4xl border-t border-border bg-card overflow-hidden max-h-[92vh] overflow-y-auto no-scrollbar"
                    >
                        <div className="relative aspect-[16/10] bg-gradient-to-br from-primary/20 to-accent">
                            {cover && <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />}
                            <button
                                onClick={onClose}
                                data-testid="listing-detail-close"
                                className="absolute top-3 right-3 h-9 w-9 rounded-full bg-background/90 grid place-items-center backdrop-blur"
                            >
                                <X className="h-5 w-5" strokeWidth={2.5} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-display text-2xl font-black">{listing.title}</div>
                                    <div className="mt-1 text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
                                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {listing.city}</span>
                                        <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {listing.move_in_date}</span>
                                    </div>
                                </div>
                                <div className="rounded-2xl bg-accent text-accent-foreground px-3 py-2 text-right">
                                    <div className="font-black text-lg text-gradient-purple">₹{(listing.rent || 0).toLocaleString("en-IN")}</div>
                                    <div className="text-[10px] font-semibold uppercase tracking-wide">per month</div>
                                </div>
                            </div>
                            {listing.description && <p className="mt-4 text-sm leading-relaxed text-foreground/90">{listing.description}</p>}
                            {!!listing.amenities?.length && (
                                <div className="mt-4">
                                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Amenities</div>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {listing.amenities.map((a) => (
                                            <span key={a} className="vibe-pill text-xs">{a.replace(/_/g, " ")}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <Button className="mt-6 h-14 w-full rounded-2xl font-semibold lift" data-testid="listing-interested-btn" onClick={onClose}>
                                <Sparkles className="mr-2 h-5 w-5" strokeWidth={2.5} /> I'm interested
                            </Button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
